import { Component } from "@theme/component";
import { fetchConfig, onAnimationEnd, preloadImage } from "@theme/utilities";
import {
  ThemeEvents,
  CartAddEvent,
  CartErrorEvent,
  VariantUpdateEvent,
  QuantitySelectorUpdateEvent,
} from "@theme/events";
import { cartPerformance } from "@theme/performance";
import { morph } from "@theme/morph";

export const ADD_TO_CART_TEXT_ANIMATION_DURATION = 2000;

/**
 * A custom element that manages an add to cart button.
 *
 * @typedef {object} AddToCartRefs
 * @property {HTMLButtonElement} addToCartButton - The add to cart button.
 * @extends Component<AddToCartRefs>
 */
export class AddToCartComponent extends Component {
  requiredRefs = ["addToCartButton"];

  /** @type {number | undefined} */
  #animationTimeout;

  /** @type {number | undefined} */
  #cleanupTimeout;

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener("pointerenter", this.#preloadImage);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    if (this.#animationTimeout) clearTimeout(this.#animationTimeout);
    if (this.#cleanupTimeout) clearTimeout(this.#cleanupTimeout);
    this.removeEventListener("pointerenter", this.#preloadImage);
  }

  /**
   * Disables the add to cart button.
   */
  disable() {
    this.refs.addToCartButton.disabled = true;
  }

  /**
   * Enables the add to cart button.
   */
  enable() {
    this.refs.addToCartButton.disabled = false;
  }

  /**
   * Handles the click event for the add to cart button.
   * @param {MouseEvent & {target: HTMLElement}} event - The click event.
   */
  handleClick(event) {
    this.animateAddToCart();

    if (!event.target.closest(".quick-add-modal")) this.#animateFlyToCart();
  }

  #preloadImage = () => {
    const image = this.dataset.productVariantMedia;

    if (!image) return;

    preloadImage(image);
  };

  /**
   * Animates the fly to cart animation.
   */
  #animateFlyToCart() {
    const { addToCartButton } = this.refs;
    const cartIcon = document.querySelector(".header-actions__cart-icon");

    const image = this.dataset.productVariantMedia;

    if (!cartIcon || !addToCartButton || !image) return;

    const flyToCartElement = /** @type {FlyToCart} */ (
      document.createElement("fly-to-cart")
    );

    flyToCartElement.style.setProperty("background-image", `url(${image})`);
    flyToCartElement.source = addToCartButton;
    flyToCartElement.destination = cartIcon;

    document.body.appendChild(flyToCartElement);
  }

  /**
   * Animates the add to cart button.
   */
  animateAddToCart() {
    const { addToCartButton } = this.refs;

    if (this.#animationTimeout) clearTimeout(this.#animationTimeout);
    if (this.#cleanupTimeout) clearTimeout(this.#cleanupTimeout);

    if (!addToCartButton.classList.contains("atc-added")) {
      addToCartButton.classList.add("atc-added");
    }

    this.#animationTimeout = setTimeout(() => {
      this.#cleanupTimeout = setTimeout(() => {
        this.refs.addToCartButton.classList.remove("atc-added");
      }, 10);
    }, ADD_TO_CART_TEXT_ANIMATION_DURATION);
  }
}

if (!customElements.get("add-to-cart-component")) {
  customElements.define("add-to-cart-component", AddToCartComponent);
}

/**
 * A custom element that manages a product form.
 *
 * @typedef {object} ProductFormRefs
 * @property {HTMLInputElement} variantId - The form input for submitting the variant ID.
 * @property {AddToCartComponent | undefined} addToCartButtonContainer - The add to cart button container element.
 * @property {HTMLElement | undefined} addToCartTextError - The add to cart text error.
 * @property {HTMLElement | undefined} acceleratedCheckoutButtonContainer - The accelerated checkout button container element.
 * @property {HTMLElement} liveRegion - The live region.
 *
 * @extends Component<ProductFormRefs>
 */
class ProductFormComponent extends Component {
  requiredRefs = ["variantId", "liveRegion"];
  #abortController = new AbortController();

  /** @type {number | undefined} */
  #timeout;

  /** @type {string | undefined} */
  #defaultAddToCartText;
  /** @type {string | undefined} */
  #defaultSoldOutText;
  /** @type {string | undefined} */
  #defaultUnavailableText;

  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#abortController;
    const target = this.closest(".shopify-section, dialog, product-card");
    target?.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate, {
      signal,
    });
    target?.addEventListener(
      ThemeEvents.variantSelected,
      this.#onVariantSelected,
      { signal },
    );

    this.#captureAddToCartTexts();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
  }

  /** @type {{ variants?: Array<Record<string, any>>, options_with_values?: Array<Record<string, any>>, options?: string[] } | undefined} */
  #productData;

  /** @type {Promise<void> | undefined} */
  #productDataPromise;

  /** @type {Map<string, { quantity: number; policy?: string; management?: string }>} */
  #variantInventoryCache = new Map();

  /**
   * Handles the submit event for the product form.
   *
   * @param {Event} event - The submit event.
   */
  async handleSubmit(event) {
    const { addToCartTextError } = this.refs;
    // Stop default behaviour from the browser
    event.preventDefault();

    if (this.#timeout) clearTimeout(this.#timeout);

    // Check if the add to cart button is disabled and do an early return if it is
    if (
      this.refs.addToCartButtonContainer?.refs.addToCartButton?.getAttribute(
        "disabled",
      ) === "true"
    )
      return;

    // Send the add to cart information to the cart
    const form = this.querySelector("form");

    if (!form) throw new Error("Product form element missing");

    const formData = new FormData(form);
    const requestedQuantity =
      Number(formData.get("quantity")) ||
      Number(this.dataset.quantityDefault) ||
      0;

    const cartItemsComponents = document.querySelectorAll(
      "cart-items-component",
    );
    const cartItemComponentsSectionIds = [];
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        cartItemComponentsSectionIds.push(item.dataset.sectionId);
      }
    });

    const sectionsParam = cartItemComponentsSectionIds.join(",");
    const baseEntries = Array.from(formData.entries());

    try {
      const submissions = await this.#createSubmissionPayloads(
        baseEntries,
        sectionsParam,
        requestedQuantity,
      );

      let totalQuantityAdded = 0;
      /** @type {any} */
      let lastResponse;
      /** @type {string | undefined} */
      let lastVariantId;

      for (const submission of submissions) {
        const fetchCfg = fetchConfig("javascript", {
          body: submission.formData,
        });

        const response = await fetch(Theme.routes.cart_add_url, {
          ...fetchCfg,
          headers: {
            ...fetchCfg.headers,
            Accept: "text/html",
          },
        });

        const responseData = await response.json();

        if (responseData.status) {
          window.dispatchEvent(
            new CartErrorEvent(this.id, responseData.message),
          );

          if (addToCartTextError) {
            addToCartTextError.classList.remove("hidden");

            // Reuse the text node if the user is spam-clicking
            const textNode = addToCartTextError.childNodes[2];
            if (textNode) {
              textNode.textContent = responseData.message;
            } else {
              const newTextNode = document.createTextNode(responseData.message);
              addToCartTextError.appendChild(newTextNode);
            }

            // Create or get existing error live region for screen readers
            this.#setLiveRegionText(responseData.message);

            this.#timeout = setTimeout(() => {
              if (!addToCartTextError) return;
              addToCartTextError.classList.add("hidden");

              // Clear the announcement
              this.#clearLiveRegionText();
            }, 10000);
          }

          this.dispatchEvent(
            new CartAddEvent({}, this.id, {
              didError: true,
              source: "product-form-component",
              itemCount: this.#getCartCountFromResponse(responseData),
              quantityAdded: requestedQuantity,
              productId: this.dataset.productId,
            }),
          );

          return;
        }

        totalQuantityAdded += submission.quantity;
        lastResponse = responseData;
        lastVariantId = submission.variantId ?? lastVariantId;
      }

      if (!lastResponse) {
        return;
      }

      const quantityAdded = totalQuantityAdded || requestedQuantity;

      if (addToCartTextError) {
        addToCartTextError.classList.add("hidden");
        addToCartTextError.removeAttribute("aria-live");
      }

      // Add aria-live region to inform screen readers that the item was added
      if (this.refs.addToCartButtonContainer?.refs.addToCartButton) {
        const addToCartButton =
          this.refs.addToCartButtonContainer.refs.addToCartButton;
        const addedTextElement = addToCartButton.querySelector(
          ".add-to-cart-text--added",
        );
        const addedText =
          addedTextElement?.textContent?.trim() || Theme.translations.added;

        this.#setLiveRegionText(addedText);

        setTimeout(() => {
          this.#clearLiveRegionText();
        }, 5000);
      }

      const sourceId =
        lastVariantId || (formData.get("id")?.toString() ?? this.id);

      this.dispatchEvent(
        new CartAddEvent({}, sourceId, {
          source: "product-form-component",
          itemCount: this.#getCartCountFromResponse(lastResponse),
          quantityAdded,
          productId: this.dataset.productId,
          sections: lastResponse.sections,
        }),
      );

      this.#resetQuantitySelector();
    } catch (error) {
      console.error(error);
    } finally {
      // add more thing to do in here if needed.
      cartPerformance.measureFromEvent("add:user-action", event);
    }
  }

  /**
   * Reset the quantity selector input after a successful add.
   */
  #resetQuantitySelector() {
    const quantityInput = this.querySelector('input[name="quantity"]');
    if (!(quantityInput instanceof HTMLInputElement)) return;

    const defaultValue =
      Number(this.dataset.quantityDefault) || Number(quantityInput.min) || 1;

    quantityInput.value = `${defaultValue}`;
    quantityInput.dispatchEvent(
      new QuantitySelectorUpdateEvent(
        defaultValue,
        Number(quantityInput.dataset.cartLine),
      ),
    );
  }

  /**
   * @param {*} text
   */
  #setLiveRegionText(text) {
    const liveRegion = this.refs.liveRegion;
    liveRegion.textContent = text;
  }

  #clearLiveRegionText() {
    const liveRegion = this.refs.liveRegion;
    liveRegion.textContent = "";
  }

  /**
   * Retrieves the cart item count from the add-to-cart response when available.
   * @param {unknown} response - The add-to-cart response payload.
   * @returns {number | undefined}
   */
  #getCartCountFromResponse(response) {
    const possibleCount =
      response?.item_count ??
      response?.cart_count ??
      response?.cart?.item_count;
    const parsedCount =
      typeof possibleCount === "number" ? possibleCount : Number(possibleCount);

    return Number.isFinite(parsedCount) ? parsedCount : undefined;
  }

  /**
   * @param {VariantUpdateEvent} event
   */
  #onVariantUpdate = (event) => {
    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    } else if (event.detail.data.productId !== this.dataset.productId) {
      return;
    }

    const { variantId, addToCartButtonContainer } = this.refs;

    const currentAddToCartButton =
      addToCartButtonContainer?.refs.addToCartButton;
    const newAddToCartButton = event.detail.data.html.querySelector(
      '[ref="addToCartButton"]',
    );

    if (!currentAddToCartButton) return;

    // Update the button state
    if (
      event.detail.resource == null ||
      event.detail.resource.available == false
    ) {
      addToCartButtonContainer.disable();
      this.refs.acceleratedCheckoutButtonContainer?.setAttribute(
        "hidden",
        "true",
      );
    } else {
      addToCartButtonContainer.enable();
      this.refs.acceleratedCheckoutButtonContainer?.removeAttribute("hidden");
    }

    // Update the add to cart button text and icon
    if (newAddToCartButton) {
      morph(currentAddToCartButton, newAddToCartButton);
    }

    if (this.#isOnProductPage()) {
      this.#syncAddToCartState(event.detail.resource);
    }

    // Update the variant ID
    variantId.value = event.detail.resource.id ?? "";

    // Set the data attribute for the add to cart button to the product variant media if it exists
    if (event.detail.resource) {
      const productVariantMedia =
        event.detail.resource.featured_media?.preview_image?.src;
      productVariantMedia &&
        addToCartButtonContainer?.setAttribute(
          "data-product-variant-media",
          productVariantMedia + "&width=100",
        );
    }
  };

  /**
   * Disable the add to cart button while the UI is updating before #onVariantUpdate is called.
   * Accelerated checkout button is also disabled via its own event listener not exposed to the theme.
   */
  #onVariantSelected = () => {
    this.refs.addToCartButtonContainer?.disable();
  };

  #captureAddToCartTexts() {
    const button = this.refs.addToCartButtonContainer?.refs.addToCartButton;
    if (!(button instanceof HTMLButtonElement)) return;

    this.#defaultAddToCartText =
      button.dataset.addToCartText ||
      button.querySelector(".add-to-cart-text__content")?.textContent?.trim();
    this.#defaultSoldOutText =
      button.dataset.soldOutText || this.#defaultSoldOutText;
    this.#defaultUnavailableText =
      button.dataset.unavailableText || this.#defaultUnavailableText;
  }

  #getTextFallback(kind) {
    const lang = (document.documentElement.lang || "").toLowerCase();
    const isDe = lang.startsWith("de");
    if (kind === "add") return isDe ? "In den Warenkorb" : "Add to cart";
    if (kind === "sold") return isDe ? "Ausverkauft" : "Sold out";
    return isDe ? "Nicht verfügbar" : "Unavailable";
  }

  #syncQuantitySelectorState(available) {
    const selectors = Array.from(
      this.querySelectorAll("quantity-selector-component"),
    );
    if (!selectors.length) return;

    for (const selector of selectors) {
      const input = selector.querySelector('input[name="quantity"]');
      if (input instanceof HTMLInputElement) {
        input.disabled = !available;
      }

      const buttons = selector.querySelectorAll(
        'button[name="minus"], button[name="plus"]',
      );
      for (const button of buttons) {
        if (button instanceof HTMLButtonElement) {
          button.disabled = !available;
        }
      }
    }
  }

  #syncAddToCartState(resource) {
    const { addToCartButtonContainer } = this.refs;
    const button = addToCartButtonContainer?.refs.addToCartButton;
    if (!(button instanceof HTMLButtonElement)) return;

    const textEl = button.querySelector(".add-to-cart-text__content");

    const addText =
      button.dataset.addToCartText ||
      this.#defaultAddToCartText ||
      this.#getTextFallback("add");
    const soldText =
      button.dataset.soldOutText ||
      this.#defaultSoldOutText ||
      this.#getTextFallback("sold");
    const unavailableText =
      button.dataset.unavailableText ||
      this.#defaultUnavailableText ||
      this.#getTextFallback("unavailable");

    if (!resource) {
      addToCartButtonContainer?.disable();
      this.refs.acceleratedCheckoutButtonContainer?.setAttribute(
        "hidden",
        "true",
      );
      this.#syncQuantitySelectorState(false);
      if (textEl) textEl.textContent = unavailableText;
      return;
    }

    if (resource.available === false) {
      addToCartButtonContainer?.disable();
      this.refs.acceleratedCheckoutButtonContainer?.setAttribute(
        "hidden",
        "true",
      );
      this.#syncQuantitySelectorState(false);
      if (textEl) textEl.textContent = soldText;
      return;
    }

    addToCartButtonContainer?.enable();
    this.refs.acceleratedCheckoutButtonContainer?.removeAttribute("hidden");
    this.#syncQuantitySelectorState(true);
    if (textEl) textEl.textContent = addText;
  }

  #isOnProductPage() {
    return (
      !this.closest("product-card") &&
      !this.closest("quick-add-component") &&
      !this.closest("quick-add-dialog")
    );
  }

  /**
   * Returns Storefront API config for this instance.
   * @returns {{ token: string, apiVersion: string, endpoint: string }}
   */
  #getStorefrontConfig() {
    const token = (this.dataset.storefrontToken || "").trim();
    const apiVersion = (this.dataset.storefrontApiVersion || "2025-01").trim();
    const endpoint = `/api/${apiVersion}/graphql.json`;

    return { token, apiVersion, endpoint };
  }

  /**
   * Ensures the product data is loaded.
   * @returns {Promise<void> | undefined}
   */
  #ensureProductData() {
    if (
      this.#productData ||
      this.#productDataPromise ||
      !this.dataset.productId
    ) {
      return this.#productDataPromise;
    }

    const productId = this.dataset.productId;
    const cacheKey = `${productId}`;
    const cached =
      window.__storefrontVariantsCache &&
      window.__storefrontVariantsCache[cacheKey];
    if (cached?.variants?.length) {
      this.#productData = cached;
      return Promise.resolve();
    }

    const storefrontConfig = this.#getStorefrontConfig();
    if (!storefrontConfig.token) {
      console.warn(
        "[ProductForm][Data] Missing Storefront token; skipping variant fetch.",
      );
      return undefined;
    }

    const gid =
      typeof productId === "string" && productId.startsWith("gid://")
        ? productId
        : `gid://shopify/Product/${productId}`;

    const queryWithInventory = `
      query GetAllVariants($id: ID!, $cursor: String) {
        product(id: $id) {
          variants(first: 250, after: $cursor) {
            edges {
              node {
                id
                availableForSale
                title
                selectedOptions { name value }
                quantityAvailable
                sku
                barcode
              }
              cursor
            }
            pageInfo { hasNextPage }
          }
        }
      }
    `;

    const queryWithoutInventory = `
      query GetAllVariants($id: ID!, $cursor: String) {
        product(id: $id) {
          variants(first: 250, after: $cursor) {
            edges {
              node {
                id
                availableForSale
                title
                selectedOptions { name value }
                sku
                barcode
              }
              cursor
            }
            pageInfo { hasNextPage }
          }
        }
      }
    `;

    const runQuery = async (query) => {
      const allVariants = [];
      let hasNextPage = true;
      let cursor = null;

      while (hasNextPage) {
        const response = await fetch(storefrontConfig.endpoint, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "X-Shopify-Storefront-Access-Token": storefrontConfig.token,
          },
          body: JSON.stringify({ query, variables: { id: gid, cursor } }),
        });

        if (!response.ok) {
          throw new Error(
            `Storefront query failed: ${response.status} ${response.statusText}`,
          );
        }

        const json = await response.json();
        if (Array.isArray(json?.errors) && json.errors.length) {
          const err = new Error(
            json.errors[0]?.message || "Storefront query error",
          );
          err.errors = json.errors;
          throw err;
        }

        const connection = json?.data?.product?.variants;
        if (!connection) break;

        connection.edges.forEach((edge) => {
          if (edge?.node) allVariants.push(edge.node);
        });

        hasNextPage = Boolean(connection.pageInfo?.hasNextPage);
        cursor = hasNextPage
          ? (connection.edges[connection.edges.length - 1]?.cursor ?? null)
          : null;
      }

      return allVariants;
    };

    console.debug("[ProductForm][Data] Fetching Storefront variants", gid);

    this.#productDataPromise = runQuery(queryWithInventory)
      .catch((error) => {
        const message = (error?.message || "").toLowerCase();
        const errors = error?.errors;
        const inventoryAccessError =
          message.includes("quantityavailable") ||
          message.includes("unauthenticated_read_product_inventory") ||
          (Array.isArray(errors) &&
            errors.some((e) =>
              (e?.message || "").toLowerCase().includes("quantityavailable"),
            ));

        if (inventoryAccessError) {
          console.warn(
            "[ProductForm][Data] Storefront inventory scope missing; retrying without quantityAvailable",
          );
          return runQuery(queryWithoutInventory);
        }

        throw error;
      })
      .then((variants) => {
        this.#productData = {
          variants: this.#normalizeApiVariants(variants),
        };

        if (cacheKey) {
          window.__storefrontVariantsCache =
            window.__storefrontVariantsCache || {};
          window.__storefrontVariantsCache[cacheKey] = this.#productData;
        }

        console.debug(
          "[ProductForm][Data] Loaded FULL variant set from Storefront:",
          this.#productData.variants.length,
        );
      })
      .catch((error) => {
        this.#productData = undefined;
        throw error;
      })
      .finally(() => {
        this.#productDataPromise = undefined;
      });

    return this.#productDataPromise;
  }

  /**
   * Normalizes variants returned from the custom API into the Shopify JS product format.
   * @param {Array<Record<string, any>>} variants
   * @returns {Array<Record<string, any>>}
   */
  #normalizeApiVariants(variants) {
    if (!Array.isArray(variants)) return [];

    const extractNumericId = (value) => {
      if (typeof value !== "string") return null;
      const numeric = value.split("/").pop();
      if (numeric && !Number.isNaN(Number(numeric))) return Number(numeric);
      return null;
    };

    const parseId = (id) => {
      const direct = extractNumericId(id);
      if (direct != null) return direct;
      if (typeof id !== "string") return id;
      try {
        const decoded = atob(id);
        const decodedId = extractNumericId(decoded);
        if (decodedId != null) return decodedId;
      } catch {
        // noop
      }
      return id;
    };

    return variants.map((variant) => {
      const selectedOptions = Array.isArray(variant?.selectedOptions)
        ? variant.selectedOptions.map((opt) => opt?.value)
        : [];

      const option1 = variant.option1 ?? selectedOptions[0];
      const option2 = variant.option2 ?? selectedOptions[1];
      const option3 = variant.option3 ?? selectedOptions[2];

      const policy = (variant.inventoryPolicy ?? variant.inventory_policy)
        ?.toString()
        .toLowerCase();
      const management = (
        variant.inventoryManagement ?? variant.inventory_management
      )
        ?.toString()
        .toLowerCase();

      const normalizeOptionValue = (value) =>
        typeof value === "string"
          ? value.toLowerCase().replace(/[\s\-_]/g, "")
          : "";

      const optionValues = [
        option1,
        option2,
        option3,
        ...selectedOptions,
      ].filter((value) => typeof value === "string" && value.length);

      const isPreorder = optionValues.some((value) => {
        const key = normalizeOptionValue(value);
        return key === "vorbestellung" || key === "preorder";
      });

      return {
        ...variant,
        id: parseId(variant.id),
        available:
          typeof variant.available === "boolean"
            ? variant.available
            : isPreorder
              ? true
              : Boolean(variant.availableForSale),
        option1,
        option2,
        option3,
        inventory_quantity:
          variant.inventory_quantity ??
          variant.inventoryQuantity ??
          variant.quantityAvailable,
        inventory_policy: isPreorder ? "continue" : policy,
        inventory_management: management || "shopify",
      };
    });
  }

  /** Reads server-rendered inventory snapshot (Liquid JSON) for this product */
  #getServerInventorySnapshot() {
    const pid = this.dataset.productId || this.#productData?.id;

    if (!pid) {
      console.debug(
        "[ProductForm][Snapshot] Missing product id; cannot locate snapshot",
      );
      return null;
    }

    const el = document.getElementById(`variant-inventory-${pid}`);
    if (!el) {
      console.debug("[ProductForm][Snapshot] No snapshot element for", pid);
      return null;
    }
    try {
      const parsed = JSON.parse(el.textContent || "[]");
      console.debug(
        "[ProductForm][Snapshot] Parsed snapshot",
        pid,
        "variants=",
        Array.isArray(parsed) ? parsed.length : "∅",
      );
      return parsed;
    } catch {
      console.warn("[ProductForm][Snapshot] Failed to parse snapshot JSON");
      return null;
    }
  }

  /** Merge snapshot truth into #productData.variants by id */
  #mergeInventorySnapshot() {
    if (!this.#productData) {
      console.debug("[ProductForm][Snapshot] No product data to merge");
      return null;
    }

    if (!Array.isArray(this.#productData.variants)) {
      this.#productData.variants = [];
    }
    const snap = this.#getServerInventorySnapshot();
    if (!Array.isArray(snap) || !snap.length) {
      console.debug("[ProductForm][Snapshot] No snapshot variants to merge");
      return null;
    }

    const normalizeQty = (value, fallback) =>
      Number.isFinite(+value) ? +value : fallback;

    const byId = new Map(
      this.#productData.variants.map((v) => [String(v.id), v]),
    );

    let added = 0;
    let updated = 0;

    for (const snapVariant of snap) {
      const id = String(snapVariant.id);
      const target = byId.get(id);

      if (target) {
        updated += 1;
        target.inventory_quantity = normalizeQty(
          snapVariant.inventory_quantity,
          target.inventory_quantity,
        );
        target.inventory_policy =
          snapVariant.inventory_policy ?? target.inventory_policy;
        target.inventory_management =
          snapVariant.inventory_management ?? target.inventory_management;
        target.available =
          typeof snapVariant.available === "boolean"
            ? snapVariant.available
            : target.available;
        target.option1 = snapVariant.option1 ?? target.option1;
        target.option2 = snapVariant.option2 ?? target.option2;
        target.option3 = snapVariant.option3 ?? target.option3;
      } else {
        added += 1;
        const normalized = {
          id: snapVariant.id,
          available:
            typeof snapVariant.available === "boolean"
              ? snapVariant.available
              : undefined,
          option1: snapVariant.option1,
          option2: snapVariant.option2,
          option3: snapVariant.option3,
          inventory_quantity: normalizeQty(
            snapVariant.inventory_quantity,
            undefined,
          ),
          inventory_policy: snapVariant.inventory_policy ?? undefined,
          inventory_management: snapVariant.inventory_management ?? undefined,
        };
        this.#productData.variants.push(normalized);
        byId.set(id, normalized);
      }
    }

    const stats = {
      snapshotVariants: snap.length,
      added,
      updated,
      productVariants: this.#productData.variants.length,
    };

    console.debug("[ProductForm][Snapshot] Applied merge", stats);

    return stats;
  }

  /**
   * Hydrates inventory information for variants that report zero quantity despite being available.
   * Shopify sometimes omits up-to-date inventory on product JSON payloads, so we fetch the variant
   * endpoint directly as a fallback.
   * @param {Array<Record<string, any>>} variants - The variants to hydrate.
   * @returns {Promise<void>}
   */
  /**
   * Hydrates inventory information for variants (force-refresh when zeros/stale).
   * - Always refetch when:
   *   - quantity is not a finite number OR equals 0
   *   - OR inventory_policy is missing/empty
   *   - OR inventory_management is missing/empty
   * - Otherwise, trust cache briefly.
   * Logs BEFORE → AFTER so we can verify correctness.
   * @param {Array<Record<string, any>>} variants
   * @returns {Promise<void>}
   */

  async #hydrateVariantInventories(variants) {
    const snapshot = this.#getServerInventorySnapshot();
    if (Array.isArray(snapshot) && snapshot.length) return;
    if (!Array.isArray(variants) || variants.length === 0) return;

    const now = Date.now();

    /** @type {Promise<void>[]} */
    const tasks = [];

    for (const v of variants) {
      if (!v || v.id == null) continue;

      const id = `${v.id}`;
      const beforeQty = Number.isFinite(Number(v.inventory_quantity))
        ? Number(v.inventory_quantity)
        : NaN;
      const beforePolicy = (v.inventory_policy ?? "").toString();
      const beforeMgmt = (v.inventory_management ?? "").toString();

      const hasStorefrontQty =
        Number.isFinite(beforeQty) && v.quantityAvailable != null;
      if (hasStorefrontQty) {
        const policyFallback = beforePolicy || "deny";
        const mgmtFallback = beforeMgmt || "shopify";
        v.inventory_policy = policyFallback;
        v.inventory_management = mgmtFallback;
        this.#variantInventoryCache.set(id, {
          quantity: beforeQty,
          policy: policyFallback,
          management: mgmtFallback,
        });
        continue;
      }

      // Decide whether to refetch
      const cached = this.#variantInventoryCache.get(id);
      const cachedQty = cached?.quantity;
      const cachedPolicy = cached?.policy ?? "";
      const cachedMgmt = cached?.management ?? "";

      // If we previously cached a 0, don't trust it—force refresh.
      const needFreshBecauseZero =
        !Number.isFinite(beforeQty) || beforeQty === 0 || cachedQty === 0;

      const needFreshBecauseMissingPolicyOrMgmt =
        !beforePolicy || !beforeMgmt || !cachedPolicy || !cachedMgmt;

      // If variant is inventory-managed and policy isn’t 'continue', we need the real qty;
      // When policy is 'continue' (oversell), quantity can be 0 and still add to cart, but we still fetch once to confirm.
      const manages = beforeMgmt === "shopify" || cachedMgmt === "shopify";
      const policyLower = (beforePolicy || cachedPolicy || "").toLowerCase();
      const needFreshBecauseManaged = manages && policyLower !== "continue";

      const mustFetch =
        !cached ||
        needFreshBecauseZero ||
        needFreshBecauseMissingPolicyOrMgmt ||
        needFreshBecauseManaged;

      // Log BEFORE snapshot
      console.log(
        `[ProductForm][Hydrate][BEFORE] id=${id} name="${
          v.option3 ?? "N/A"
        }" qty=${Number.isFinite(beforeQty) ? beforeQty : "∅"} policy="${
          beforePolicy || "∅"
        }" mgmt="${beforeMgmt || "∅"}" fetch=${mustFetch ? "YES" : "no"}`,
      );

      if (!mustFetch) continue;

      tasks.push(
        fetch(`/variants/${id}.js?ts=${now}`, { cache: "no-store" })
          .then((res) => {
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            return res.json();
          })
          .then((data) => {
            const q = Number(data?.inventory_quantity);
            const pol = (data?.inventory_policy ?? "").toString();
            const mg = (data?.inventory_management ?? "").toString();

            if (Number.isFinite(q)) v.inventory_quantity = q;
            if (pol) v.inventory_policy = pol;
            if (mg) v.inventory_management = mg;

            this.#variantInventoryCache.set(id, {
              quantity: Number.isFinite(q) ? q : 0,
              policy: v.inventory_policy,
              management: v.inventory_management,
            });

            console.log(
              `[ProductForm][Hydrate][AFTER ] id=${id} name="${
                v.option3 ?? "N/A"
              }" qty=${Number.isFinite(q) ? q : "∅"} policy="${
                pol || "∅"
              }" mgmt="${mg || "∅"}"`,
            );
          })
          .catch((err) => {
            console.warn(
              `[ProductForm][Hydrate] Failed to load variant ${id}`,
              err,
            );
            // still cache *something* so later passes don’t loop forever
            const fallbackQty = Number.isFinite(beforeQty) ? beforeQty : 0;
            this.#variantInventoryCache.set(id, {
              quantity: fallbackQty,
              policy: beforePolicy || cachedPolicy || "",
              management: beforeMgmt || cachedMgmt || "",
            });
          }),
      );
    }

    if (tasks.length) await Promise.all(tasks);
  }

  /**
   * Builds the submission payloads for the cart request.
   * @param {[string, FormDataEntryValue][]} baseEntries - The base form entries.
   * @param {string} sectionsParam - The sections parameter.
   * @param {number} requestedQuantity - The requested quantity from the form.
   * @returns {Promise<Array<{ formData: FormData; quantity: number; variantId?: string }>>}
   */
  async #createSubmissionPayloads(
    baseEntries,
    sectionsParam,
    requestedQuantity,
  ) {
    const selectedVariantId = baseEntries.find(([key]) => key === "id")?.[1];
    const variantIdString =
      typeof selectedVariantId === "string"
        ? selectedVariantId
        : selectedVariantId?.toString();

    if (!variantIdString || requestedQuantity <= 0) {
      return [
        {
          formData: this.#createFormData(
            baseEntries,
            sectionsParam,
            variantIdString ?? "",
            requestedQuantity || 1,
          ),
          quantity: requestedQuantity || 1,
          variantId: variantIdString ?? undefined,
        },
      ];
    }

    const splitPlan = await this.#buildSplitPlan(
      variantIdString,
      requestedQuantity,
    );

    if (!splitPlan) {
      return [
        {
          formData: this.#createFormData(
            baseEntries,
            sectionsParam,
            variantIdString,
            requestedQuantity,
          ),
          quantity: requestedQuantity,
          variantId: variantIdString,
        },
      ];
    }

    const variantMap = new Map(
      Array.isArray(this.#productData?.variants)
        ? this.#productData.variants.map((variant) => [
            `${variant.id}`,
            variant,
          ])
        : [],
    );

    console.groupCollapsed("[ProductForm][SplitPlan] Submissions overview");
    splitPlan.forEach((item, index) => {
      const variant = variantMap.get(`${item.id}`);
      console.log(
        `Submission #${index + 1}: batch "${
          variant?.option3 ?? "N/A"
        }" (variant ${item.id}), quantity ${item.quantity}`,
      );
    });
    console.groupEnd();

    return splitPlan.map((item) => ({
      formData: this.#createFormData(
        baseEntries,
        sectionsParam,
        item.id,
        item.quantity,
      ),
      quantity: item.quantity,
      variantId: item.id,
    }));
  }

  /**
   * Creates a FormData instance for a submission.
   * @param {[string, FormDataEntryValue][]} baseEntries - The base form entries.
   * @param {string} sectionsParam - The sections parameter.
   * @param {string} variantId - The variant id for the submission.
   * @param {number} quantity - The quantity for the submission.
   * @returns {FormData}
   */
  #createFormData(baseEntries, sectionsParam, variantId, quantity) {
    const submission = new FormData();

    for (const [key, value] of baseEntries) {
      if (key === "id" || key === "quantity" || key === "sections") continue;
      submission.append(key, value);
    }

    if (variantId) {
      submission.set("id", variantId);
    }

    const sanitizedQuantity = quantity > 0 ? quantity : 1;
    submission.set("quantity", `${sanitizedQuantity}`);

    if (sectionsParam) {
      submission.set("sections", sectionsParam);
    }

    return submission;
  }

  /**
   * Builds the split plan for distributing the requested quantity across option 3 values.
   * @param {string} selectedVariantId - The selected variant id.
   * @param {number} requestedQuantity - The requested quantity from the form.
   * @returns {Promise<Array<{ id: string; quantity: number }> | null>}
   */
  /**
   * Builds the split plan for distributing the requested quantity across option 3 values.
   * @param {string} selectedVariantId - The selected variant id.
   * @param {number} requestedQuantity - The requested quantity from the form.
   * @returns {Promise<Array<{ id: string; quantity: number }> | null>}
   */
  async #buildSplitPlan(selectedVariantId, requestedQuantity) {
    if (!this.dataset.productUrl) {
      console.debug(
        "[ProductForm][SplitPlan]",
        "Missing product URL; skipping split plan.",
      );
      return null;
    }

    if (requestedQuantity <= 0) {
      console.debug(
        "[ProductForm][SplitPlan]",
        "Requested quantity is zero or negative; skipping split plan.",
      );
      return null;
    }

    await this.#ensureProductData();
    const mergeStats = this.#mergeInventorySnapshot();
    if (mergeStats) {
      console.debug("[ProductForm][SplitPlan] Snapshot merge", mergeStats);
    }

    const productData = this.#productData;
    if (!productData || !Array.isArray(productData.variants)) {
      console.warn(
        "[ProductForm][SplitPlan]",
        "Product data unavailable; cannot build split plan.",
      );
      return null;
    }

    const variants = productData.variants;
    console.debug("[ProductForm][SplitPlan] Variant pool", variants.length);
    const variantMap = new Map(variants.map((v) => [`${v.id}`, v]));
    const selectedVariant = variants.find(
      (v) => `${v.id}` === `${selectedVariantId}`,
    );

    if (!selectedVariant) {
      console.warn(
        "[ProductForm][SplitPlan]",
        "Selected variant not found in product data.",
      );
      return null;
    }

    // ---- Inventory gate: only split when requested > selected batch stock
    const selectedInventory = Math.max(
      Number(selectedVariant.inventory_quantity) || 0,
      0,
    );
    if (requestedQuantity <= selectedInventory) {
      console.debug(
        "[ProductForm][SplitPlan]",
        `Requested quantity (${requestedQuantity}) within selected variant inventory (${selectedInventory}). Using single submission.`,
      );
      return null;
    }

    // ---- Figure out option names/values
    // Supports both shapes:
    // A) productData.options_with_values (array of objects)
    // B) productData.options[2].values for shops that don’t expose options_with_values in .js
    const option1Value = selectedVariant.option1;
    const option2Value = selectedVariant.option2;

    if (!option1Value || !option2Value) {
      console.debug(
        "[ProductForm][SplitPlan]",
        "Missing option1/option2; skipping split plan.",
      );
      return null;
    }

    let option3Name;
    let option3ValuesOrder = [];

    const thirdOptionFromOWV = Array.isArray(productData.options_with_values)
      ? productData.options_with_values.find(
          (o) =>
            o?.position === 3 ||
            (productData.options?.[2] &&
              o?.name ===
                (typeof productData.options[2] === "string"
                  ? productData.options[2]
                  : productData.options[2]?.name)),
        )
      : undefined;

    if (
      thirdOptionFromOWV &&
      Array.isArray(thirdOptionFromOWV.values) &&
      thirdOptionFromOWV.values.length
    ) {
      option3Name = thirdOptionFromOWV.name;
      option3ValuesOrder = thirdOptionFromOWV.values.map((v) =>
        typeof v === "string" ? v : v?.name,
      );
    } else {
      const o = Array.isArray(productData.options)
        ? productData.options[2]
        : undefined;
      if (typeof o === "string") {
        option3Name = o;
      } else if (o && typeof o === "object") {
        option3Name = o.name;
        if (Array.isArray(o.values)) {
          option3ValuesOrder = o.values.map((v) =>
            typeof v === "string" ? v : v?.name,
          );
        }
      }
    }

    let matchingVariants = [];
    if (option3ValuesOrder && option3ValuesOrder.length) {
      // Normal case: use defined option3 order
      matchingVariants = variants.filter(
        (v) =>
          v.option1 === option1Value &&
          v.option2 === option2Value &&
          option3ValuesOrder.includes(v.option3),
      );
    } else {
      // Fallback: no explicit option3 list → split across siblings with same option1+option2
      console.debug(
        "[ProductForm][SplitPlan]",
        "No third option list; using fallback across same option1+option2.",
      );
      matchingVariants = variants.filter(
        (v) => v.option1 === option1Value && v.option2 === option2Value,
      );
    }

    console.debug(
      "[ProductForm][SplitPlan] Matching variants",
      matchingVariants.length,
      {
        option1: option1Value,
        option2: option2Value,
        option3Name: option3Name ?? "<unknown>",
      },
    );

    if (matchingVariants.length <= 1) {
      console.debug(
        "[ProductForm][SplitPlan]",
        "Only one matching variant; no split required.",
      );
      return null;
    }

    await this.#hydrateVariantInventories(matchingVariants);

    // Sort candidates: by option3 order when present; otherwise by higher stock, then original index
    const variantIndexMap = new Map(variants.map((v, i) => [v.id, i]));
    const getOption3Index = (val) => {
      if (!option3ValuesOrder || !option3ValuesOrder.length)
        return Number.POSITIVE_INFINITY;
      const idx = option3ValuesOrder.indexOf(val);
      return idx === -1 ? Number.POSITIVE_INFINITY : idx;
    };

    matchingVariants.sort((a, b) => {
      const aIdx = getOption3Index(a.option3);
      const bIdx = getOption3Index(b.option3);
      if (aIdx !== bIdx) return aIdx - bIdx;

      const aStock = Number(a.inventory_quantity) || 0;
      const bStock = Number(b.inventory_quantity) || 0;
      if (aStock !== bStock) return bStock - aStock;

      const orderA = variantIndexMap.get(a.id) ?? Number.POSITIVE_INFINITY;
      const orderB = variantIndexMap.get(b.id) ?? Number.POSITIVE_INFINITY;
      return orderA - orderB;
    });

    // ---- Allocation (stock-first, then pre-order fallback) ----------------------
    const splitPlan = [];
    let remaining = requestedQuantity;
    let allocated = 0;

    console.groupCollapsed("[ProductForm][SplitPlan] Allocation progress");
    console.log(
      `[ProductForm][SplitPlan] Requested quantity: ${requestedQuantity} pcs`,
    );
    console.log(
      `[ProductForm][SplitPlan] Selected variant ${selectedVariantId} (${
        option3Name ?? "opt3"
      }="${selectedVariant.option3 ?? "N/A"}")`,
    );

    // 1) Collect a single pre-order candidate (if any), but don't allocate to it yet.
    //    Treat option3 value "pre-order" (case-insensitive, ignoring space/dash/underscore) as the preferred oversell bucket.
    //    If none named "pre-order", fall back to any variant that allows overselling (managed && !deny) or is not inventory managed.
    const normalize = (s) =>
      typeof s === "string" ? s.toLowerCase().replace(/[\s\-_]/g, "") : "";
    const PREFERRED_PREORDER = "preorder";

    let preOrderCandidate = null;

    // 2) First pass: allocate only to strict stock-managed, deny-backorder variants with available > 0
    for (const v of matchingVariants) {
      if (remaining <= 0) break;
      // Inventory flags (robust to missing policy)
      const vManages = v.inventory_management === "shopify";
      const rawPolicy = (v.inventory_policy ?? "").toLowerCase(); // 'deny' | 'continue' | ''
      // If the variant is inventory-managed but policy is missing, assume 'deny' (finite stock).
      const policy = vManages ? rawPolicy || "deny" : "";
      const vDeny = policy === "deny";
      const allowsOversell = vManages && policy === "continue";
      const notManaged = !vManages;

      const vName = v.option3 ?? "N/A";
      const isNamedPreorder = normalize(v.option3) === PREFERRED_PREORDER;

      // Pick a fallback ONLY for explicit pre-order that can oversell, or any oversell candidate
      const canOversell = allowsOversell || notManaged;
      if (!preOrderCandidate && isNamedPreorder && canOversell) {
        preOrderCandidate = v;
      } else if (!preOrderCandidate && canOversell) {
        preOrderCandidate = v;
      }

      // Debug (keep!)
      console.log(
        `[ProductForm][SplitPlan] Inspect "${vName}" → manages=${vManages}, policy=${
          policy || "∅"
        }, qty=${Number(v.inventory_quantity) || 0}`,
      );

      // Only allocate here if the variant is stock-managed AND denies backorders (classic finite stock)
      if (vManages && vDeny) {
        const available = Math.max(Number(v.inventory_quantity) || 0, 0);
        if (available > 0) {
          const assignable = Math.min(available, remaining);
          splitPlan.push({ id: `${v.id}`, quantity: assignable });
          remaining -= assignable;
          allocated += assignable;
          console.log(
            `[ProductForm][SplitPlan] "${vName}" alloc ${assignable} (avail ${available}).`,
          );
        } else {
          console.log(`[ProductForm][SplitPlan] Skip "${vName}" (0 stock).`);
        }
      } else {
        console.log(
          `[ProductForm][SplitPlan] Defer "${vName}" (${
            allowsOversell
              ? "allows oversell"
              : notManaged
                ? "not managed"
                : "other"
          });`,
        );
      }
    }

    // 3) If still remaining, and we have a pre-order candidate, push the rest to that one
    if (remaining > 0 && preOrderCandidate) {
      const vName = preOrderCandidate.option3 ?? "N/A";
      splitPlan.push({ id: `${preOrderCandidate.id}`, quantity: remaining });
      console.log(
        `[ProductForm][SplitPlan] Fallback to "${vName}" with remaining ${remaining}.`,
      );
      allocated += remaining;
      remaining = 0;
    }

    console.log(
      `[ProductForm][SplitPlan] Allocation result: ${allocated} / ${requestedQuantity}.`,
    );
    console.groupEnd();

    if (allocated < requestedQuantity) {
      console.warn(
        "[ProductForm][SplitPlan]",
        "Unable to allocate full quantity with available batches. Falling back to single submission.",
      );
      return null;
    }

    if (splitPlan.length === 1 && splitPlan[0].id === `${selectedVariantId}`) {
      console.debug(
        "[ProductForm][SplitPlan]",
        "Split plan resulted in a single submission matching the selected variant. Using default submission.",
      );
      return null;
    }

    console.groupCollapsed("[ProductForm][SplitPlan] Final plan");
    splitPlan.forEach((item, i) => {
      const v = variantMap.get(`${item.id}`);
      console.log(
        `Step ${i + 1}: "${v?.option3 ?? "N/A"}" (variant ${item.id}) -> ${
          item.quantity
        }`,
      );
    });
    console.groupEnd();

    return splitPlan;
  }
}

if (!customElements.get("product-form-component")) {
  customElements.define("product-form-component", ProductFormComponent);
}

class FlyToCart extends HTMLElement {
  /** @type {Element} */
  source;

  /** @type {Element} */
  destination;

  connectedCallback() {
    this.#animate();
  }

  #animate() {
    const rect = this.getBoundingClientRect();
    const sourceRect = this.source.getBoundingClientRect();
    const destinationRect = this.destination.getBoundingClientRect();

    //Define bezier curve points
    // Maybe add half of the size of the flying thingy to the x and y to make it center properly
    const offset = {
      x: rect.width / 2,
      y: rect.height / 2,
    };
    const startPoint = {
      x: sourceRect.left + sourceRect.width / 2 - offset.x,
      y: sourceRect.top + sourceRect.height / 2 - offset.y,
    };

    const endPoint = {
      x: destinationRect.left + destinationRect.width / 2 - offset.x,
      y: destinationRect.top + destinationRect.height / 2 - offset.y,
    };

    //Calculate the control points
    const controlPoint1 = { x: startPoint.x, y: startPoint.y - 200 }; // Go up 200px
    const controlPoint2 = { x: endPoint.x - 300, y: endPoint.y - 100 }; // Go left 300px and up 100px

    //Animation variables
    /** @type {number | null} */
    let startTime = null;
    const duration = 600; // 600ms

    this.style.opacity = "1";

    /**
     * Animates the flying thingy along the bezier curve.
     * @param {number} currentTime - The current time.
     */
    const animate = (currentTime) => {
      if (!startTime) startTime = currentTime;
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);

      // Calculate current position along the bezier curve
      const position = bezierPoint(
        progress,
        startPoint,
        controlPoint1,
        controlPoint2,
        endPoint,
      );

      //Update the position of the flying thingy
      this.style.setProperty("--x", `${position.x}px`);
      this.style.setProperty("--y", `${position.y}px`);

      // Scale down as it approaches the cart
      const scale = 1 - progress * 0.5;
      this.style.setProperty("--scale", `${scale}`);

      //Continue the animation if not finished
      if (progress < 1) {
        requestAnimationFrame(animate);
      } else {
        //Fade out the flying thingy
        this.style.opacity = "0";
        onAnimationEnd(this, () => this.remove());
      }
    };

    // Position the flying thingy back to the start point
    this.style.setProperty("--x", `${startPoint.x}px`);
    this.style.setProperty("--y", `${startPoint.y}px`);

    //Start the animation
    requestAnimationFrame(animate);
  }
}

/**
 * Calculates a point on a cubic Bézier curve.
 * @param {number} t - The parameter value (0 <= t <= 1).
 * @param {{x: number, y: number}} p0 - The starting point (x, y).
 * @param {{x: number, y: number}} p1 - The first control point (x, y).
 * @param {{x: number, y: number}} p2 - The second control point (x, y).
 * @param {{x: number, y: number}} p3 - The ending point (x, y).
 * @returns {{x: number, y: number}} The point on the curve.
 */
function bezierPoint(t, p0, p1, p2, p3) {
  const cX = 3 * (p1.x - p0.x);
  const bX = 3 * (p2.x - p1.x) - cX;
  const aX = p3.x - p0.x - cX - bX;

  const cY = 3 * (p1.y - p0.y);
  const bY = 3 * (p2.y - p1.y) - cY;
  const aY = p3.y - p0.y - cY - bY;

  const x = aX * Math.pow(t, 3) + bX * Math.pow(t, 2) + cX * t + p0.x;
  const y = aY * Math.pow(t, 3) + bY * Math.pow(t, 2) + cY * t + p0.y;

  return { x, y };
}

if (!customElements.get("fly-to-cart")) {
  customElements.define("fly-to-cart", FlyToCart);
}
